import { Fragment, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { useNicknames } from '../lib/useNicknames.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface OverviewData {
  totalSkus: number;
  outOfStock: number;
  lowStock: number;
  stranded: number;
  monthlyStorageCost: number;
  criticalRestock: number;
  urgentSkus: UrgentSku[];
}

interface UrgentSku {
  asin: string;
  skus: string;
  title: string;
  currentStock: number;
  inbound: number;
  avgDailyVelocity: number;
  daysUntilStockout: number | null;
  urgency: string;
}

interface ForecastRow {
  asin: string;
  skus: string;
  title: string;
  parentAsin?: string | null;
  parentNickname?: string | null;
  marketplace: string;
  currentStock: number;
  localStock: number;
  remoteStock: number;
  inbound: number;
  avgDailyVelocity: number;
  velocityHistory?: number[] | null;
  velocityStddev?: number | null;
  lastYearVelocity?: number | null;
  seasonalDeltaPct?: number | null;
  daysUntilStockout: number | null;
  p90DaysUntilStockout?: number | null;
  reorderByDate: string | null;
  suggestedQty: number | null;
  lostSalesDailyUsd?: number | null;
  cogs?: number | null;
  cogsCurrency?: string | null;
  price?: number | null;
  currency?: string | null;
  amazon: { action: string | null; qty: number | null; daysOfSupply: number | null; alert: string | null };
  isMonitored: boolean;
  urgency: string;
  /** True if this row is a parent-ASIN rollup of multiple child rows */
  isParentRollup?: boolean;
  /** Original child rows if this is a rollup */
  children?: ForecastRow[];
}

interface ForecastResponse {
  forecasts: ForecastRow[];
  marketplaces: string[];
}

const MARKETPLACE_LABELS: Record<string, string> = {
  ATVPDKIKX0DER: 'US',
  A2EUQ1WTGCTBG2: 'CA',
  A1AM78C64UM0Y8: 'MX',
  A1F83G8C2ARO7P: 'UK',
  A13V1IB3VIYZZH: 'FR',
  A1PA6795UKMFR9: 'DE',
  APJ6JRA9NG5V4: 'IT',
  A1RKKUPIHCS9HS: 'ES',
  A1805IZSGTT6HS: 'NL',
  A2NODRKZP88ZB9: 'SE',
};

interface Account {
  id: string;
  name: string;
}

// ── Column configuration ────────────────────────────────────────────────────

type SortKey = 'urgency' | 'daysUntilStockout' | 'currentStock' | 'localStock' | 'remoteStock'
  | 'inbound' | 'avgDailyVelocity' | 'seasonalDeltaPct' | 'p90DaysUntilStockout'
  | 'lostSalesDailyUsd' | 'reorderByDate' | 'suggestedQty' | 'marketplace' | 'product';

interface ColDef {
  key: string;
  label: string;
  align: 'left' | 'right' | 'center';
  defaultVisible: boolean;
  sortKey?: SortKey;
}

const ALL_COLUMNS: ColDef[] = [
  { key: 'monitored', label: 'Mon', align: 'center', defaultVisible: true },
  { key: 'product', label: 'Product', align: 'left', defaultVisible: true, sortKey: 'product' },
  { key: 'marketplace', label: 'Mkt', align: 'center', defaultVisible: true, sortKey: 'marketplace' },
  { key: 'currentStock', label: 'Total', align: 'right', defaultVisible: true, sortKey: 'currentStock' },
  { key: 'localStock', label: 'Local', align: 'right', defaultVisible: true, sortKey: 'localStock' },
  { key: 'remoteStock', label: 'Remote', align: 'right', defaultVisible: false, sortKey: 'remoteStock' },
  { key: 'inbound', label: 'Inbound', align: 'right', defaultVisible: true, sortKey: 'inbound' },
  { key: 'avgDailyVelocity', label: 'Vel/day', align: 'right', defaultVisible: true, sortKey: 'avgDailyVelocity' },
  { key: 'velocityTrend', label: 'Trend', align: 'center', defaultVisible: true },
  { key: 'seasonalDeltaPct', label: 'vs LY', align: 'right', defaultVisible: true, sortKey: 'seasonalDeltaPct' },
  { key: 'daysUntilStockout', label: 'Days Left', align: 'right', defaultVisible: true, sortKey: 'daysUntilStockout' },
  { key: 'p90DaysUntilStockout', label: 'P90 Days', align: 'right', defaultVisible: false, sortKey: 'p90DaysUntilStockout' },
  { key: 'lostSalesDaily', label: 'Lost/day', align: 'right', defaultVisible: true, sortKey: 'lostSalesDailyUsd' },
  { key: 'reorderByDate', label: 'Reorder By', align: 'right', defaultVisible: true, sortKey: 'reorderByDate' },
  { key: 'suggestedQty', label: 'Suggested', align: 'right', defaultVisible: true, sortKey: 'suggestedQty' },
  { key: 'amazonAction', label: 'Amazon', align: 'left', defaultVisible: true },
  { key: 'urgency', label: 'Urgency', align: 'center', defaultVisible: true, sortKey: 'urgency' },
  { key: 'actions', label: '', align: 'right', defaultVisible: true },
];

const COL_STORAGE_KEY = 'sellvana_forecast_columns_v3';

interface ColConfig { visible: string[]; order: string[] }

function loadColConfig(): ColConfig {
  try {
    const raw = localStorage.getItem(COL_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ColConfig;
      // Merge in any newly-added columns that weren't in saved config
      const savedKeys = new Set(parsed.order);
      const newKeys = ALL_COLUMNS.filter(c => !savedKeys.has(c.key)).map(c => c.key);
      return {
        visible: [...parsed.visible, ...newKeys.filter(k => ALL_COLUMNS.find(c => c.key === k)?.defaultVisible)],
        order: [...parsed.order, ...newKeys],
      };
    }
  } catch { /* */ }
  return {
    visible: ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key),
    order: ALL_COLUMNS.map(c => c.key),
  };
}

function saveColConfig(cfg: ColConfig) {
  localStorage.setItem(COL_STORAGE_KEY, JSON.stringify(cfg));
}

// ── Component ────────────────────────────────────────────────────────────────

const URGENCY_LEVELS: Array<{ key: string; label: string }> = [
  { key: '', label: 'All' },
  { key: 'critical', label: 'Critical' },
  { key: 'warning', label: 'Warning' },
  { key: 'monitor', label: 'Monitor' },
  { key: 'healthy', label: 'Healthy' },
  { key: 'no-sales', label: 'No sales' },
];

export function InventoryOverview() {
  const [overview, setOverview] = useState<OverviewData | null>(null);
  const [forecasts, setForecasts] = useState<ForecastRow[]>([]);
  const [availableMarketplaces, setAvailableMarketplaces] = useState<string[]>([]);
  const [accountIds, setAccountIds] = useState<string[]>([]);
  const [selectedMarketplace, setSelectedMarketplace] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { displayName, nickname, parentNickname } = useNicknames();

  const [search, setSearch] = useState('');
  const [urgencyFilter, setUrgencyFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('daysUntilStockout');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [colConfig, setColConfig] = useState<ColConfig>(loadColConfig);
  const [editingRow, setEditingRow] = useState<string | null>(null); // `${asin}-${marketplace}`
  const [view, setView] = useState<'table' | 'calendar'>('table');
  const [groupByParent, setGroupByParent] = useState(false);

  useEffect(() => {
    api.get<Account[]>('/accounts').then((accts) => {
      setAccountIds(accts.map(a => a.id));
      if (accts.length === 0) setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadData = async (mpFilter?: string) => {
    if (accountIds.length === 0) return;
    setLoading(true);
    setError('');
    const mp = mpFilter ?? selectedMarketplace;
    const mpParam = mp ? `&marketplace=${mp}` : '';

    try {
      const results = await Promise.all(
        accountIds.map(async (id) => {
          const [ov, fc] = await Promise.all([
            api.get<OverviewData>(`/inventory/overview/${id}?${mpParam.replace('&', '')}`),
            api.get<ForecastResponse>(`/inventory/forecast/${id}?limit=500${mpParam}`),
          ]);
          return { ov, fc };
        }),
      );

      const merged: OverviewData = {
        totalSkus: 0, outOfStock: 0, lowStock: 0, stranded: 0,
        monthlyStorageCost: 0, criticalRestock: 0, urgentSkus: [],
      };
      const allForecasts: ForecastRow[] = [];
      const allMps = new Set<string>();

      for (const { ov, fc } of results) {
        merged.totalSkus += ov.totalSkus;
        merged.outOfStock += ov.outOfStock;
        merged.lowStock += ov.lowStock;
        merged.stranded += ov.stranded;
        merged.monthlyStorageCost += ov.monthlyStorageCost;
        merged.criticalRestock += ov.criticalRestock;
        allForecasts.push(...fc.forecasts);
        for (const mp2 of fc.marketplaces) allMps.add(mp2);
      }

      setOverview(merged);
      setForecasts(allForecasts);
      if (allMps.size > 0 && availableMarketplaces.length === 0) {
        setAvailableMarketplaces(Array.from(allMps));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (accountIds.length > 0) loadData();
  }, [accountIds, selectedMarketplace]);

  async function toggleMonitored(row: ForecastRow) {
    const newVal = !row.isMonitored;
    const skus = row.skus.split(',').map(s => s.trim());
    try {
      await Promise.all(
        skus.map(s => api.patch('/inventory/catalog/monitored', {
          sku: s,
          marketplace: row.marketplace,
          isMonitored: newVal,
        })),
      );
      setForecasts(prev => prev.map(f =>
        f.asin === row.asin && f.marketplace === row.marketplace
          ? { ...f, isMonitored: newVal }
          : f,
      ));
      const mp = selectedMarketplace;
      const mpParam = mp ? `?marketplace=${mp}` : '';
      const ovResults = await Promise.all(
        accountIds.map(id => api.get<OverviewData>(`/inventory/overview/${id}${mpParam}`)),
      );
      const merged: OverviewData = {
        totalSkus: 0, outOfStock: 0, lowStock: 0, stranded: 0,
        monthlyStorageCost: 0, criticalRestock: 0, urgentSkus: [],
      };
      for (const ov of ovResults) {
        merged.totalSkus += ov.totalSkus;
        merged.outOfStock += ov.outOfStock;
        merged.lowStock += ov.lowStock;
        merged.stranded += ov.stranded;
        merged.monthlyStorageCost += ov.monthlyStorageCost;
        merged.criticalRestock += ov.criticalRestock;
      }
      setOverview(merged);
    } catch {
      // silently fail
    }
  }

  // ── Sort + filter pipeline ──────────────────────────────────────────────

  const visibleCols = colConfig.order
    .filter(k => colConfig.visible.includes(k))
    .map(k => ALL_COLUMNS.find(c => c.key === k)!)
    .filter(Boolean);

  const filteredSorted = useMemo(() => {
    let rows = forecasts;

    if (urgencyFilter) {
      rows = rows.filter(r => r.urgency === urgencyFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase().trim();
      rows = rows.filter(r => {
        if (r.asin?.toLowerCase().includes(q)) return true;
        if (r.title?.toLowerCase().includes(q)) return true;
        if (r.skus?.toLowerCase().includes(q)) return true;
        const skuList = r.skus.split(',').map(s => s.trim());
        for (const sku of skuList) {
          if (displayName(sku)?.toLowerCase().includes(q)) return true;
          if (parentNickname(sku)?.toLowerCase().includes(q)) return true;
        }
        return false;
      });
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    const urgencyOrder: Record<string, number> = {
      critical: 0, warning: 1, monitor: 2, healthy: 3, 'no-sales': 4,
    };

    const sorted = [...rows].sort((a, b) => {
      switch (sortBy) {
        case 'urgency':
          return dir * ((urgencyOrder[a.urgency] ?? 99) - (urgencyOrder[b.urgency] ?? 99));
        case 'daysUntilStockout':
          return dir * ((a.daysUntilStockout ?? 99999) - (b.daysUntilStockout ?? 99999));
        case 'currentStock':
          return dir * (a.currentStock - b.currentStock);
        case 'localStock':
          return dir * (a.localStock - b.localStock);
        case 'remoteStock':
          return dir * (a.remoteStock - b.remoteStock);
        case 'inbound':
          return dir * (a.inbound - b.inbound);
        case 'avgDailyVelocity':
          return dir * (a.avgDailyVelocity - b.avgDailyVelocity);
        case 'seasonalDeltaPct':
          return dir * ((a.seasonalDeltaPct ?? -9999) - (b.seasonalDeltaPct ?? -9999));
        case 'p90DaysUntilStockout':
          return dir * ((a.p90DaysUntilStockout ?? 99999) - (b.p90DaysUntilStockout ?? 99999));
        case 'lostSalesDailyUsd':
          return dir * ((a.lostSalesDailyUsd ?? 0) - (b.lostSalesDailyUsd ?? 0));
        case 'reorderByDate': {
          const av = a.reorderByDate ?? '9999-99-99';
          const bv = b.reorderByDate ?? '9999-99-99';
          return dir * av.localeCompare(bv);
        }
        case 'suggestedQty':
          return dir * ((a.suggestedQty ?? 0) - (b.suggestedQty ?? 0));
        case 'marketplace':
          return dir * a.marketplace.localeCompare(b.marketplace);
        case 'product': {
          const av = a.title ?? a.asin ?? '';
          const bv = b.title ?? b.asin ?? '';
          return dir * av.localeCompare(bv);
        }
        default:
          return 0;
      }
    });

    if (!groupByParent) return sorted;

    // Rollup: aggregate children with the same (parent_asin, marketplace) into one row.
    // Rows without a parent_asin pass through unchanged.
    const groups = new Map<string, ForecastRow[]>();
    const passthrough: ForecastRow[] = [];
    for (const r of sorted) {
      const parent = r.parentAsin?.trim();
      if (!parent) { passthrough.push(r); continue; }
      const key = `${parent}|${r.marketplace}`;
      const arr = groups.get(key) ?? [];
      arr.push(r);
      groups.set(key, arr);
    }

    const rolled: ForecastRow[] = [];
    for (const children of groups.values()) {
      if (children.length === 1) { rolled.push(children[0]); continue; }
      const first = children[0];
      const sumVel = children.reduce((s, r) => s + r.avgDailyVelocity, 0);
      const sumLocal = children.reduce((s, r) => s + r.localStock, 0);
      const sumRemote = children.reduce((s, r) => s + r.remoteStock, 0);
      const sumInbound = children.reduce((s, r) => s + r.inbound, 0);
      const sumCurrent = children.reduce((s, r) => s + r.currentStock, 0);
      const sumSuggested = children.reduce((s, r) => s + (r.suggestedQty ?? 0), 0) || null;

      // Weighted LY velocity: sum current LY over children, compute parent seasonal delta
      const lyTotal = children.reduce((s, r) => s + (r.lastYearVelocity ?? 0), 0);
      const lyAvailable = children.some(r => r.lastYearVelocity != null);
      const seasonalDelta = (lyAvailable && lyTotal > 0 && sumVel > 0)
        ? Math.round(((sumVel - lyTotal) / lyTotal) * 100)
        : null;

      // Earliest reorder date + worst urgency across children
      const urgencyOrder: Record<string, number> = { critical: 0, warning: 1, monitor: 2, healthy: 3, 'no-sales': 4 };
      const worstUrgency = children.reduce((w, r) =>
        (urgencyOrder[r.urgency] ?? 99) < (urgencyOrder[w] ?? 99) ? r.urgency : w,
        children[0].urgency);
      const earliestReorder = children
        .map(r => r.reorderByDate)
        .filter((d): d is string => !!d)
        .sort()[0] ?? null;
      const minDays = children
        .map(r => r.daysUntilStockout)
        .filter((d): d is number => d != null)
        .reduce((m, v) => Math.min(m, v), Infinity);

      // Cash cost aggregation: sum cogs * suggestedQty across children
      const childrenWithCogs = children.filter(r => r.cogs != null && r.suggestedQty != null);
      const totalSuggestedCost = childrenWithCogs.length > 0
        ? childrenWithCogs.reduce((s, r) => s + (r.cogs! * (r.suggestedQty ?? 0)), 0)
        : null;
      // Weighted avg cogs for display
      const avgCogs = childrenWithCogs.length > 0
        ? childrenWithCogs.reduce((s, r) => s + r.cogs!, 0) / childrenWithCogs.length
        : null;
      // Lost sales sum
      const totalLostSales = children.reduce((s, r) => s + (r.lostSalesDailyUsd ?? 0), 0) || null;
      // P90 worst-case days (min across children)
      const childP90s = children.map(r => r.p90DaysUntilStockout).filter((v): v is number => v != null);
      const worstP90 = childP90s.length > 0 ? Math.min(...childP90s) : null;

      rolled.push({
        asin: first.parentAsin!,
        skus: children.map(r => r.skus).join(','),
        title: first.parentNickname ?? first.parentAsin!,
        parentAsin: first.parentAsin,
        parentNickname: first.parentNickname,
        marketplace: first.marketplace,
        currentStock: sumCurrent,
        localStock: sumLocal,
        remoteStock: sumRemote,
        inbound: sumInbound,
        avgDailyVelocity: Number(sumVel.toFixed(1)),
        velocityHistory: null,  // not meaningful when aggregated
        velocityStddev: null,
        lastYearVelocity: lyAvailable ? Number(lyTotal.toFixed(1)) : null,
        seasonalDeltaPct: seasonalDelta,
        daysUntilStockout: minDays === Infinity ? null : minDays,
        p90DaysUntilStockout: worstP90,
        reorderByDate: earliestReorder,
        suggestedQty: sumSuggested,
        lostSalesDailyUsd: totalLostSales,
        cogs: avgCogs,
        cogsCurrency: first.cogsCurrency,
        price: first.price,
        currency: first.currency,
        amazon: {
          action: children.find(r => r.amazon.action)?.amazon.action ?? null,
          qty: children.reduce((s, r) => s + (r.amazon.qty ?? 0), 0) || null,
          daysOfSupply: null,
          alert: children.find(r => r.amazon.alert)?.amazon.alert ?? null,
        },
        isMonitored: children.some(r => r.isMonitored),
        urgency: worstUrgency,
        isParentRollup: true,
        children,
      });
      // Keep totalSuggestedCost reference to silence linter
      void totalSuggestedCost;
    }
    return [...rolled, ...passthrough];
  }, [forecasts, search, urgencyFilter, sortBy, sortDir, displayName, parentNickname, groupByParent]);

  function toggleSort(key: SortKey) {
    if (sortBy === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDir(key === 'urgency' || key === 'daysUntilStockout' ? 'asc' : 'desc');
    }
  }

  if (loading) return <LoadingSkeleton />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-base font-semibold">Inventory Intelligence</h2>
        <p className="text-sm text-gray-500">Stock levels, restock urgency, and forecasts — deduplicated by ASIN.</p>
      </div>

      {/* Country selector */}
      {availableMarketplaces.length > 1 && (
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedMarketplace('')}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
              !selectedMarketplace
                ? 'border-brand-500 bg-brand-50 text-brand-700'
                : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
          >
            All
          </button>
          {availableMarketplaces.map(mp => (
            <button
              key={mp}
              onClick={() => setSelectedMarketplace(mp === selectedMarketplace ? '' : mp)}
              className={`rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors ${
                selectedMarketplace === mp
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              <MarketplaceBadge id={mp} />
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {overview && (
        <>
          {/* KPI Cards */}
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
            <StatCard label="Total ASINs" value={String(overview.totalSkus)} />
            <StatCard label="Out of Stock" value={String(overview.outOfStock)} color={overview.outOfStock > 0 ? 'red' : 'green'} />
            <StatCard label="Low Stock" value={String(overview.lowStock)} color={overview.lowStock > 0 ? 'yellow' : 'green'} />
            <StatCard label="Stranded" value={String(overview.stranded)} color={overview.stranded > 0 ? 'orange' : 'green'} />
            <StatCard label="Monthly Storage" value={fmtCurrency(overview.monthlyStorageCost)} />
            <StatCard label="Critical Restock" value={String(overview.criticalRestock)} color={overview.criticalRestock > 0 ? 'red' : 'green'} />
          </div>

          {/* Forecast Table */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold">Restock Forecast {view === 'calendar' ? '— Reorder Calendar' : '— Grouped by ASIN'}</h3>
                <p className="text-xs text-gray-400 mt-0.5">KPIs and warnings only count monitored SKUs. Toggle off items you don't restock.</p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex rounded-lg border border-gray-300 p-0.5 text-xs">
                  <button
                    onClick={() => setView('table')}
                    className={`rounded-md px-2.5 py-1 font-medium transition-colors ${
                      view === 'table' ? 'bg-brand-50 text-brand-700' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    By SKU
                  </button>
                  <button
                    onClick={() => setView('calendar')}
                    className={`rounded-md px-2.5 py-1 font-medium transition-colors ${
                      view === 'calendar' ? 'bg-brand-50 text-brand-700' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Reorder Calendar
                  </button>
                </div>
                <label className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1.5 text-xs text-gray-600 cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={groupByParent}
                    onChange={e => setGroupByParent(e.target.checked)}
                    className="rounded border-gray-300 text-brand-600 h-3.5 w-3.5"
                  />
                  Group by parent
                </label>
                <input
                  type="text"
                  placeholder="Search SKU / ASIN / name..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs w-56"
                />
                {view === 'table' && <ColumnConfig config={colConfig} onChange={setColConfig} />}
              </div>
            </div>

            {/* Urgency filter chips */}
            <div className="mt-3 flex flex-wrap gap-1.5">
              {URGENCY_LEVELS.map(lvl => {
                const count = lvl.key
                  ? forecasts.filter(f => f.urgency === lvl.key && f.isMonitored).length
                  : forecasts.filter(f => f.isMonitored).length;
                return (
                  <button
                    key={lvl.key || 'all'}
                    onClick={() => setUrgencyFilter(lvl.key === urgencyFilter ? '' : lvl.key)}
                    className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition-colors ${
                      urgencyFilter === lvl.key
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {lvl.label}
                    <span className="ml-1 text-gray-400">{count}</span>
                  </button>
                );
              })}
              {(search || urgencyFilter) && (
                <span className="self-center text-[11px] text-gray-400 ml-2">
                  showing {filteredSorted.length} of {forecasts.length}
                </span>
              )}
            </div>

            {forecasts.length === 0 ? (
              <p className="mt-4 text-sm text-gray-500">No FBA inventory data. Run an inventory sync first.</p>
            ) : filteredSorted.length === 0 ? (
              <p className="mt-4 text-sm text-gray-500">No SKUs match current filters.</p>
            ) : view === 'calendar' ? (
              <ReorderCalendar
                rows={filteredSorted}
                displayName={displayName}
                nickname={nickname}
                parentNickname={parentNickname}
              />
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      {visibleCols.map(col => {
                        const alignClass = col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left';
                        const widthClass = col.key === 'monitored' ? 'w-10' : '';
                        const sortable = !!col.sortKey;
                        return (
                          <th
                            key={col.key}
                            className={`pb-2 text-xs font-medium text-gray-500 ${alignClass} ${widthClass} ${sortable ? 'cursor-pointer select-none hover:text-gray-700' : ''}`}
                            onClick={sortable ? () => toggleSort(col.sortKey!) : undefined}
                          >
                            {col.label}
                            {sortable && sortBy === col.sortKey && (
                              <span className="ml-1 text-[10px] text-gray-400">{sortDir === 'asc' ? '▲' : '▼'}</span>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredSorted.map((r) => {
                      const rowKey = `${r.asin}-${r.marketplace}`;
                      const isEditing = editingRow === rowKey;
                      return (
                        <Fragment key={rowKey}>
                          <tr className={r.isMonitored ? '' : 'opacity-40'}>
                            {visibleCols.map(col => (
                              <td key={col.key} className={`py-2 ${col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : ''}`}>
                                {renderCell(col.key, r, {
                                  toggleMonitored,
                                  displayName, nickname, parentNickname,
                                  editingKey: rowKey,
                                  isEditing,
                                  setEditingRow,
                                })}
                              </td>
                            ))}
                          </tr>
                          {isEditing && (
                            <tr>
                              <td colSpan={visibleCols.length} className="bg-gray-50 px-4 py-3">
                                <RowDetail
                                  row={r}
                                  accountIds={accountIds}
                                  onClose={() => setEditingRow(null)}
                                  onSaved={() => { setEditingRow(null); loadData(); }}
                                />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ── CSV export ──────────────────────────────────────────────────────────────

function csvEscape(v: unknown): string {
  if (v == null) return '';
  const s = String(v);
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadPoCsv(bucketLabel: string, rows: ForecastRow[]) {
  const header = ['ASIN', 'SKU(s)', 'Product', 'Marketplace', 'Local Stock', 'Inbound', 'Velocity/day', 'Days Left', 'Reorder By', 'Suggested Qty', 'Unit COGS', 'Total Cost', 'Amazon Action'];
  const lines = [header.join(',')];
  let grandTotal = 0;
  for (const r of rows) {
    const title = r.isParentRollup ? (r.parentNickname ?? r.parentAsin ?? r.asin) : (r.title ?? r.asin);
    const qty = r.suggestedQty ?? 0;
    const cogs = r.cogs ?? 0;
    const lineTotal = cogs * qty;
    if (qty > 0 && cogs > 0) grandTotal += lineTotal;
    lines.push([
      r.asin,
      r.skus,
      title,
      r.marketplace,
      r.localStock,
      r.inbound,
      r.avgDailyVelocity,
      r.daysUntilStockout ?? '',
      r.reorderByDate ?? '',
      qty,
      cogs ? cogs.toFixed(2) : '',
      lineTotal ? lineTotal.toFixed(2) : '',
      r.amazon.action ?? '',
    ].map(csvEscape).join(','));
  }
  if (grandTotal > 0) {
    lines.push('');
    lines.push(`,,,,,,,,,,,${grandTotal.toFixed(2)},`);
  }
  const csv = lines.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const slug = bucketLabel.replace(/[^a-z0-9]+/gi, '-').toLowerCase().replace(/^-|-$/g, '');
  a.download = `po-${slug}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Reorder Calendar view ───────────────────────────────────────────────────

interface Bucket {
  key: string;
  label: string;
  tone: 'red' | 'orange' | 'yellow' | 'blue' | 'green' | 'gray';
  rows: ForecastRow[];
}

function bucketize(rows: ForecastRow[]): Bucket[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const MS_DAY = 86_400_000;

  const buckets: Record<string, ForecastRow[]> = {
    overdue: [],
    thisWeek: [],
    nextWeek: [],
    thisMonth: [],
    next2Months: [],
    later: [],
    noReorder: [],
  };

  for (const r of rows) {
    if (!r.isMonitored) { buckets.noReorder.push(r); continue; }
    if (!r.reorderByDate || r.avgDailyVelocity === 0) { buckets.noReorder.push(r); continue; }
    const reorderDate = new Date(r.reorderByDate + 'T00:00:00');
    const daysOut = Math.round((reorderDate.getTime() - today.getTime()) / MS_DAY);
    if (daysOut < 0) buckets.overdue.push(r);
    else if (daysOut <= 7) buckets.thisWeek.push(r);
    else if (daysOut <= 14) buckets.nextWeek.push(r);
    else if (daysOut <= 30) buckets.thisMonth.push(r);
    else if (daysOut <= 60) buckets.next2Months.push(r);
    else buckets.later.push(r);
  }

  const defs: { key: string; label: string; tone: Bucket['tone'] }[] = [
    { key: 'overdue',      label: 'Overdue — reorder now',   tone: 'red' },
    { key: 'thisWeek',     label: 'This week (next 7 days)', tone: 'orange' },
    { key: 'nextWeek',     label: 'Next week (8–14 days)',   tone: 'yellow' },
    { key: 'thisMonth',    label: 'This month (15–30 days)', tone: 'blue' },
    { key: 'next2Months',  label: 'Next 1–2 months (31–60)', tone: 'green' },
    { key: 'later',        label: 'Later (60+ days)',        tone: 'gray' },
    { key: 'noReorder',    label: 'No reorder needed',       tone: 'gray' },
  ];

  return defs
    .map(d => ({ ...d, rows: buckets[d.key] ?? [] }))
    .filter(b => b.rows.length > 0);
}

const TONE_CLASSES: Record<Bucket['tone'], { border: string; bg: string; text: string; badge: string }> = {
  red:    { border: 'border-red-200',    bg: 'bg-red-50',    text: 'text-red-700',    badge: 'bg-red-100 text-red-700' },
  orange: { border: 'border-orange-200', bg: 'bg-orange-50', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-700' },
  yellow: { border: 'border-yellow-200', bg: 'bg-yellow-50', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-700' },
  blue:   { border: 'border-blue-200',   bg: 'bg-blue-50',   text: 'text-blue-700',   badge: 'bg-blue-100 text-blue-700' },
  green:  { border: 'border-green-200',  bg: 'bg-green-50',  text: 'text-green-700',  badge: 'bg-green-100 text-green-700' },
  gray:   { border: 'border-gray-200',   bg: 'bg-gray-50',   text: 'text-gray-600',   badge: 'bg-gray-100 text-gray-600' },
};

function ReorderCalendar({ rows, displayName, nickname, parentNickname }: {
  rows: ForecastRow[];
  displayName: (sku: string) => string | null;
  nickname: (sku: string) => string | null;
  parentNickname: (sku: string) => string | null;
}) {
  const buckets = useMemo(() => bucketize(rows), [rows]);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({ later: true, noReorder: true });

  if (buckets.length === 0) {
    return <p className="mt-4 text-sm text-gray-500">Nothing to reorder.</p>;
  }

  return (
    <div className="mt-4 space-y-3">
      {buckets.map(b => {
        const tone = TONE_CLASSES[b.tone];
        const totalQty = b.rows.reduce((s, r) => s + (r.suggestedQty ?? 0), 0);
        const totalCashUsd = b.rows.reduce((s, r) => s + ((r.cogs ?? 0) * (r.suggestedQty ?? 0)), 0);
        const hasCash = b.rows.some(r => r.cogs != null && r.suggestedQty != null && r.suggestedQty > 0);
        const isCollapsed = collapsed[b.key] ?? false;
        return (
          <div key={b.key} className={`rounded-lg border ${tone.border} ${tone.bg}`}>
            <div className="w-full flex items-center justify-between gap-3 px-4 py-2.5">
              <button
                onClick={() => setCollapsed(c => ({ ...c, [b.key]: !isCollapsed }))}
                className="flex items-center gap-3 text-left flex-1"
              >
                <svg className={`h-3 w-3 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-90'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
                <span className={`text-sm font-semibold ${tone.text}`}>{b.label}</span>
                <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${tone.badge}`}>
                  {b.rows.length} SKU{b.rows.length !== 1 ? 's' : ''}
                </span>
              </button>
              <div className="flex items-center gap-3">
                {totalQty > 0 && (
                  <span className="text-xs text-gray-500">
                    <span className="font-semibold text-gray-700">{totalQty.toLocaleString()}</span> units
                  </span>
                )}
                {hasCash && (
                  <span className="text-xs text-gray-500">
                    ≈ <span className="font-semibold text-gray-700">{fmtCurrency(totalCashUsd)}</span> cash
                  </span>
                )}
                {b.rows.length > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); downloadPoCsv(b.label, b.rows); }}
                    className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-[11px] font-medium text-gray-600 hover:bg-gray-50"
                    title="Download as CSV"
                  >
                    Download PO
                  </button>
                )}
              </div>
            </div>
            {!isCollapsed && (
              <div className="border-t border-gray-200 bg-white/60">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase text-gray-400">
                      <th className="px-4 py-1.5 text-left font-medium">Product</th>
                      <th className="px-2 py-1.5 text-center font-medium">Mkt</th>
                      <th className="px-2 py-1.5 text-right font-medium">Local</th>
                      <th className="px-2 py-1.5 text-right font-medium">Inbound</th>
                      <th className="px-2 py-1.5 text-right font-medium">Vel/d</th>
                      <th className="px-2 py-1.5 text-right font-medium">vs LY</th>
                      <th className="px-2 py-1.5 text-right font-medium">Days Left</th>
                      <th className="px-2 py-1.5 text-right font-medium">Reorder By</th>
                      <th className="px-2 py-1.5 text-right font-medium">Suggested</th>
                      <th className="px-2 py-1.5 text-left font-medium">Amazon</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {b.rows.map(r => {
                      const isRollup = r.isParentRollup && r.children;
                      const skus = r.skus.split(',').map(s => s.trim());
                      const firstSku = skus[0];
                      const pn = firstSku ? parentNickname(firstSku) : null;
                      const childNicks = [...new Set(skus.map(s => nickname(s)).filter(n => n && n !== pn))] as string[];
                      const childLabel = childNicks.length > 0
                        ? childNicks.join(', ')
                        : (skus.length === 1 ? displayName(skus[0]) : null);
                      return (
                        <tr key={`${r.asin}-${r.marketplace}`}>
                          <td className="px-4 py-1.5 max-w-[260px]" title={isRollup ? `${r.parentAsin} — ${r.children!.length} variants` : `${r.asin} — ${r.skus}`}>
                            {isRollup ? (
                              <>
                                <div className="truncate font-semibold text-gray-900">{r.parentNickname ?? r.parentAsin ?? r.asin}</div>
                                <div className="text-[10px] text-gray-400">{r.children!.length} variants rolled up</div>
                              </>
                            ) : (
                              <>
                                {pn && <div className="text-[10px] text-gray-400">{pn}</div>}
                                <div className="truncate font-medium text-gray-900">{childLabel ?? pn ?? r.asin}</div>
                              </>
                            )}
                          </td>
                          <td className="px-2 py-1.5 text-center"><MarketplaceBadge id={r.marketplace} /></td>
                          <td className={`px-2 py-1.5 text-right font-medium ${r.localStock === 0 ? 'text-red-600' : r.localStock <= 10 ? 'text-orange-600' : 'text-gray-700'}`}>{r.localStock}</td>
                          <td className="px-2 py-1.5 text-right text-gray-500">{r.inbound}</td>
                          <td className="px-2 py-1.5 text-right text-gray-700">{r.avgDailyVelocity}</td>
                          <td className="px-2 py-1.5 text-right">
                            {r.seasonalDeltaPct == null
                              ? <span className="text-gray-300">—</span>
                              : (() => {
                                  const pct = r.seasonalDeltaPct;
                                  const tone = pct > 5 ? 'text-green-600' : pct < -5 ? 'text-red-600' : 'text-gray-500';
                                  const arrow = pct > 5 ? '↑' : pct < -5 ? '↓' : '→';
                                  return <span className={tone}>{arrow} {pct > 0 ? '+' : ''}{pct}%</span>;
                                })()}
                          </td>
                          <td className="px-2 py-1.5 text-right font-medium text-gray-900">{r.daysUntilStockout ?? '—'}</td>
                          <td className="px-2 py-1.5 text-right text-gray-700">{r.reorderByDate ?? '—'}</td>
                          <td className="px-2 py-1.5 text-right font-semibold text-gray-900">{r.suggestedQty ?? '—'}</td>
                          <td className="px-2 py-1.5 text-left text-xs text-gray-600">{r.amazon.action ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Cell renderer ───────────────────────────────────────────────────────────

interface CellCtx {
  toggleMonitored: (row: ForecastRow) => void;
  displayName: (sku: string) => string | null;
  nickname: (sku: string) => string | null;
  parentNickname: (sku: string) => string | null;
  editingKey: string;
  isEditing: boolean;
  setEditingRow: (key: string | null) => void;
}

function renderCell(key: string, r: ForecastRow, ctx: CellCtx) {
  switch (key) {
    case 'monitored':
      return (
        <button
          onClick={() => ctx.toggleMonitored(r)}
          className={`h-4 w-4 rounded border transition-colors ${
            r.isMonitored ? 'border-brand-500 bg-brand-500 text-white' : 'border-gray-300 bg-white'
          }`}
          title={r.isMonitored ? 'Monitored — click to unmonitor' : 'Not monitored — click to monitor'}
        >
          {r.isMonitored && (
            <svg className="h-4 w-4" viewBox="0 0 16 16" fill="none">
              <path d="M4 8l3 3 5-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </button>
      );
    case 'product': {
      if (r.isParentRollup && r.children) {
        const label = r.parentNickname ?? r.parentAsin ?? r.asin;
        return (
          <div className="max-w-[200px]" title={`${r.parentAsin} — ${r.children.length} variants`}>
            <div className="truncate font-semibold text-gray-900">{label}</div>
            <div className="text-[10px] text-gray-400">{r.children.length} variants rolled up</div>
          </div>
        );
      }
      const skus = r.skus.split(',').map(s => s.trim());
      const firstSku = skus[0];
      const pn = firstSku ? ctx.parentNickname(firstSku) : null;
      const childNicks = [...new Set(skus.map(s => ctx.nickname(s)).filter(n => n && n !== pn))] as string[];
      const childLabel = childNicks.length > 0
        ? childNicks.join(', ')
        : (skus.length === 1 ? ctx.displayName(skus[0]) : null);
      return (
        <div className="max-w-[200px]" title={`${r.asin} — ${r.skus}`}>
          {pn && <div className="text-[10px] text-gray-400">{pn}</div>}
          <div className="truncate font-medium text-gray-900">
            {childLabel ?? pn ?? r.asin}
          </div>
        </div>
      );
    }
    case 'marketplace':
      return <MarketplaceBadge id={r.marketplace} />;
    case 'currentStock':
      return <span className="text-gray-700">{r.currentStock}</span>;
    case 'localStock':
      return <span className={`font-medium ${r.localStock === 0 ? 'text-red-600' : r.localStock <= 10 ? 'text-orange-600' : 'text-gray-700'}`}>{r.localStock}</span>;
    case 'remoteStock':
      return <span className="text-gray-400">{r.remoteStock || '—'}</span>;
    case 'inbound':
      return <span className="text-gray-500">{r.inbound}</span>;
    case 'avgDailyVelocity':
      return <span className="text-gray-700">{r.avgDailyVelocity}</span>;
    case 'velocityTrend':
      return <VelocitySparkline values={r.velocityHistory ?? null} />;
    case 'seasonalDeltaPct': {
      const pct = r.seasonalDeltaPct;
      if (pct == null) return <span className="text-gray-300">—</span>;
      const tone = pct > 5 ? 'text-green-600' : pct < -5 ? 'text-red-600' : 'text-gray-500';
      const arrow = pct > 5 ? '↑' : pct < -5 ? '↓' : '→';
      return <span className={tone}>{arrow} {pct > 0 ? '+' : ''}{pct}%</span>;
    }
    case 'daysUntilStockout':
      return <span className="font-medium text-gray-900">{r.daysUntilStockout ?? '—'}</span>;
    case 'p90DaysUntilStockout':
      if (r.p90DaysUntilStockout == null) return <span className="text-gray-300">—</span>;
      return (
        <span className="text-gray-600" title="Worst-case stockout at 90th percentile velocity (mean + 1.28 × stddev)">
          {r.p90DaysUntilStockout}
        </span>
      );
    case 'lostSalesDaily':
      if (r.lostSalesDailyUsd == null || r.lostSalesDailyUsd === 0) return <span className="text-gray-300">—</span>;
      return (
        <span className="font-medium text-red-600" title="Estimated daily revenue lost while out of stock (price × velocity)">
          {fmtCurrency(r.lostSalesDailyUsd)}
        </span>
      );
    case 'reorderByDate':
      return <span className="text-gray-500">{r.reorderByDate ?? '—'}</span>;
    case 'suggestedQty':
      return <span className="text-gray-700">{r.suggestedQty ?? '—'}</span>;
    case 'amazonAction': {
      const a = r.amazon;
      if (!a.action) return <span className="text-gray-300">—</span>;
      return (
        <div className="text-xs">
          <div className="font-medium text-gray-700">{a.action}</div>
          {a.qty != null && <div className="text-gray-400">qty {a.qty}{a.daysOfSupply != null ? ` · ${a.daysOfSupply}d` : ''}</div>}
        </div>
      );
    }
    case 'urgency':
      return r.isMonitored ? <UrgencyBadge urgency={r.urgency} /> : <span className="text-xs text-gray-400">—</span>;
    case 'actions':
      return (
        <button
          onClick={() => ctx.setEditingRow(ctx.isEditing ? null : ctx.editingKey)}
          className="rounded-lg border border-gray-200 px-2 py-0.5 text-[11px] text-gray-500 hover:bg-gray-50"
        >
          {ctx.isEditing ? 'Close' : 'Edit'}
        </button>
      );
    default:
      return null;
  }
}

// ── Row expansion: inbound shipments + lead-time editor ────────────────────

interface InboundShipment {
  shipment_id: string;
  shipment_name: string | null;
  status: string;
  destination_fc: string | null;
  sku: string;
  quantity_shipped: number;
  quantity_received: number;
  receiving_status: string;
  need_by_date: string | null;
  shipped_at: string | null;
  in_transit_at: string | null;
  receiving_at: string | null;
  closed_at: string | null;
}

function RowDetail({ row, accountIds, onClose, onSaved }: {
  row: ForecastRow;
  accountIds: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  return (
    <div className="space-y-4">
      <InboundShipmentsPanel row={row} accountIds={accountIds} />
      <AiForecastPanel row={row} accountIds={accountIds} />
      <LeadTimeEditor row={row} onClose={onClose} onSaved={onSaved} />
    </div>
  );
}

interface AiForecast {
  narrative: string;
  projection: null | {
    days30: { p50: number; p90: number };
    days60: { p50: number; p90: number };
    days90: { p50: number; p90: number };
  };
  stats?: {
    meanVelocity: number;
    last7Mean: number;
    prev7Mean: number | null;
    trendPct: number | null;
    stddev: number;
    seriesLength: number;
  };
}

function AiForecastPanel({ row, accountIds }: { row: ForecastRow; accountIds: string[] }) {
  const [data, setData] = useState<AiForecast | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  async function run() {
    if (accountIds.length === 0) return;
    setLoading(true);
    setErr('');
    try {
      // Try each account until one returns useful data (the asin likely belongs to one)
      for (const accountId of accountIds) {
        const resp = await api.post<AiForecast>('/inventory/ai-forecast', {
          accountId,
          asin: row.asin,
          marketplace: row.marketplace,
        });
        if (resp.projection) {
          setData(resp);
          return;
        }
        // fallback: even without projection, first account's narrative is fine
        setData(resp);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'AI forecast failed');
    } finally {
      setLoading(false);
    }
  }

  if (row.isParentRollup) {
    return (
      <div className="text-xs text-gray-400">AI forecast is per individual ASIN — expand a specific variant to use it.</div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs font-semibold text-gray-700">AI demand forecast</div>
        {!data && !loading && (
          <button
            onClick={run}
            className="rounded-lg border border-brand-300 bg-brand-50 px-2.5 py-1 text-[11px] font-medium text-brand-700 hover:bg-brand-100"
          >
            Generate forecast
          </button>
        )}
      </div>

      {loading && <div className="text-xs text-gray-500">Analyzing velocity history…</div>}
      {err && <div className="text-xs text-red-600">{err}</div>}

      {data && (
        <div className="rounded-lg border border-brand-200 bg-brand-50/50 p-3 space-y-2">
          <p className="text-xs text-gray-700 leading-relaxed">{data.narrative}</p>
          {data.projection && (
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              {[
                { label: 'Next 30 days', p: data.projection.days30 },
                { label: 'Next 60 days', p: data.projection.days60 },
                { label: 'Next 90 days', p: data.projection.days90 },
              ].map(({ label, p }) => (
                <div key={label} className="rounded bg-white px-2 py-1.5 border border-brand-100">
                  <div className="text-gray-500">{label}</div>
                  <div className="font-medium text-gray-800">
                    {p.p50.toLocaleString()}–{p.p90.toLocaleString()} units
                  </div>
                </div>
              ))}
            </div>
          )}
          {data.stats && (
            <div className="text-[10px] text-gray-500">
              Based on {data.stats.seriesLength} days of history
              {data.stats.trendPct != null && ` · trend ${data.stats.trendPct >= 0 ? '+' : ''}${data.stats.trendPct}% WoW`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function InboundShipmentsPanel({ row, accountIds }: { row: ForecastRow; accountIds: string[] }) {
  const [shipments, setShipments] = useState<InboundShipment[] | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const skus = row.skus.split(',').map(s => s.trim()).filter(Boolean);
    if (skus.length === 0 || accountIds.length === 0) {
      setShipments([]);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const results = await Promise.all(
          accountIds.map(id =>
            api.get<{ shipments: InboundShipment[] }>(
              `/inventory/inbound-shipments/${id}?skus=${encodeURIComponent(skus.join(','))}`,
            ).catch(() => ({ shipments: [] })),
          ),
        );
        if (cancelled) return;
        const merged = results.flatMap(r => r.shipments);
        setShipments(merged);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [row.skus, accountIds]);

  if (loading) {
    return <div className="text-xs text-gray-400">Checking inbound shipments…</div>;
  }
  if (!shipments || shipments.length === 0) {
    return <div className="text-xs text-gray-400">No pending inbound shipments for these SKUs.</div>;
  }

  const totalShipped = shipments.reduce((s, sh) => s + (sh.quantity_shipped ?? 0), 0);
  const totalReceived = shipments.reduce((s, sh) => s + (sh.quantity_received ?? 0), 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs font-semibold text-gray-700">
          Inbound shipments — {shipments.length} active
        </div>
        <div className="text-[11px] text-gray-500">
          {totalReceived.toLocaleString()} / {totalShipped.toLocaleString()} received
        </div>
      </div>
      <div className="overflow-x-auto rounded border border-gray-200 bg-white">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-500 bg-gray-50">
              <th className="px-2 py-1 text-left font-medium">Shipment</th>
              <th className="px-2 py-1 text-left font-medium">SKU</th>
              <th className="px-2 py-1 text-left font-medium">Status</th>
              <th className="px-2 py-1 text-left font-medium">Destination</th>
              <th className="px-2 py-1 text-right font-medium">Shipped</th>
              <th className="px-2 py-1 text-right font-medium">Received</th>
              <th className="px-2 py-1 text-left font-medium">Need by</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {shipments.map(sh => (
              <tr key={`${sh.shipment_id}-${sh.sku}`}>
                <td className="px-2 py-1 text-gray-700 font-mono text-[11px]" title={sh.shipment_name ?? sh.shipment_id}>
                  {sh.shipment_id}
                </td>
                <td className="px-2 py-1 text-gray-700 font-mono text-[11px]">{sh.sku}</td>
                <td className="px-2 py-1 text-gray-600">{sh.status}</td>
                <td className="px-2 py-1 text-gray-500">{sh.destination_fc ?? '—'}</td>
                <td className="px-2 py-1 text-right text-gray-700">{(sh.quantity_shipped ?? 0).toLocaleString()}</td>
                <td className={`px-2 py-1 text-right font-medium ${sh.receiving_status === 'complete' ? 'text-green-600' : sh.receiving_status === 'partial' ? 'text-orange-600' : 'text-gray-500'}`}>
                  {(sh.quantity_received ?? 0).toLocaleString()}
                </td>
                <td className="px-2 py-1 text-gray-500">{sh.need_by_date || sh.shipped_at?.slice(0, 10) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Lead-time / MOQ editor ──────────────────────────────────────────────────

interface SkuRestockRow {
  sku: string;
  production_lead_days: number;
  freight_lead_days: number;
  buffer_days: number;
  min_order_qty: number;
}

function LeadTimeEditor({ row, onClose, onSaved }: {
  row: ForecastRow;
  onClose: () => void;
  onSaved: () => void;
}) {
  const skus = useMemo(() => row.skus.split(',').map(s => s.trim()).filter(Boolean), [row.skus]);
  const [rows, setRows] = useState<SkuRestockRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.get<{ products: { sku: string; production_lead_days: number | null; freight_lead_days: number | null; buffer_days: number | null; min_order_qty: number | null }[] }>('/inventory/local-products');
        const bySku = new Map(resp.products.map(p => [p.sku, p]));
        setRows(skus.map(sku => {
          const p = bySku.get(sku);
          return {
            sku,
            production_lead_days: p?.production_lead_days ?? 0,
            freight_lead_days: p?.freight_lead_days ?? 0,
            buffer_days: p?.buffer_days ?? 0,
            min_order_qty: p?.min_order_qty ?? 0,
          };
        }));
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        setLoading(false);
      }
    })();
  }, [skus]);

  function updateField(sku: string, field: keyof Omit<SkuRestockRow, 'sku'>, value: number) {
    setRows(prev => prev.map(r => r.sku === sku ? { ...r, [field]: value } : r));
  }

  function applyToAll(field: keyof Omit<SkuRestockRow, 'sku'>, value: number) {
    setRows(prev => prev.map(r => ({ ...r, [field]: value })));
  }

  async function save() {
    setSaving(true);
    setErr('');
    try {
      await Promise.all(rows.map(r => api.put(`/inventory/local-products/${encodeURIComponent(r.sku)}`, {
        production_lead_days: r.production_lead_days,
        freight_lead_days: r.freight_lead_days,
        buffer_days: r.buffer_days,
        min_order_qty: r.min_order_qty,
      })));
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to save');
      setSaving(false);
    }
  }

  if (loading) return <div className="text-xs text-gray-500">Loading SKU details...</div>;
  if (err) return <div className="text-xs text-red-600">{err}</div>;

  const allSameProd = rows.every(r => r.production_lead_days === rows[0]?.production_lead_days);
  const allSameFreight = rows.every(r => r.freight_lead_days === rows[0]?.freight_lead_days);
  const allSameBuffer = rows.every(r => r.buffer_days === rows[0]?.buffer_days);
  const allSameMoq = rows.every(r => r.min_order_qty === rows[0]?.min_order_qty);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-gray-700">
          Edit restock params — {rows.length} SKU{rows.length > 1 ? 's' : ''}
        </div>
        <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-500">
              <th className="px-2 py-1 text-left font-medium">SKU</th>
              <th className="px-2 py-1 text-right font-medium" title="Production lead days">Prod days</th>
              <th className="px-2 py-1 text-right font-medium" title="Freight / shipping days">Freight days</th>
              <th className="px-2 py-1 text-right font-medium" title="Safety buffer">Buffer days</th>
              <th className="px-2 py-1 text-right font-medium">MOQ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.sku} className="border-t border-gray-200">
                <td className="px-2 py-1 text-gray-700 font-mono text-[11px]">{r.sku}</td>
                <td className="px-2 py-1">
                  <input type="number" min={0} value={r.production_lead_days}
                    onChange={e => updateField(r.sku, 'production_lead_days', Math.max(0, Number(e.target.value) || 0))}
                    className="w-16 rounded border border-gray-300 px-1 py-0.5 text-right" />
                </td>
                <td className="px-2 py-1">
                  <input type="number" min={0} value={r.freight_lead_days}
                    onChange={e => updateField(r.sku, 'freight_lead_days', Math.max(0, Number(e.target.value) || 0))}
                    className="w-16 rounded border border-gray-300 px-1 py-0.5 text-right" />
                </td>
                <td className="px-2 py-1">
                  <input type="number" min={0} value={r.buffer_days}
                    onChange={e => updateField(r.sku, 'buffer_days', Math.max(0, Number(e.target.value) || 0))}
                    className="w-16 rounded border border-gray-300 px-1 py-0.5 text-right" />
                </td>
                <td className="px-2 py-1">
                  <input type="number" min={0} value={r.min_order_qty}
                    onChange={e => updateField(r.sku, 'min_order_qty', Math.max(0, Number(e.target.value) || 0))}
                    className="w-20 rounded border border-gray-300 px-1 py-0.5 text-right" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {rows.length > 1 && (
        <div className="flex flex-wrap gap-2 text-[11px] text-gray-500">
          <span>Apply to all:</span>
          <button onClick={() => applyToAll('production_lead_days', rows[0].production_lead_days)}
            disabled={allSameProd}
            className="rounded border border-gray-300 px-1.5 py-0.5 hover:bg-gray-50 disabled:opacity-40">
            Prod
          </button>
          <button onClick={() => applyToAll('freight_lead_days', rows[0].freight_lead_days)}
            disabled={allSameFreight}
            className="rounded border border-gray-300 px-1.5 py-0.5 hover:bg-gray-50 disabled:opacity-40">
            Freight
          </button>
          <button onClick={() => applyToAll('buffer_days', rows[0].buffer_days)}
            disabled={allSameBuffer}
            className="rounded border border-gray-300 px-1.5 py-0.5 hover:bg-gray-50 disabled:opacity-40">
            Buffer
          </button>
          <button onClick={() => applyToAll('min_order_qty', rows[0].min_order_qty)}
            disabled={allSameMoq}
            className="rounded border border-gray-300 px-1.5 py-0.5 hover:bg-gray-50 disabled:opacity-40">
            MOQ
          </button>
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button onClick={onClose} className="rounded-lg border border-gray-300 px-3 py-1 text-xs text-gray-600 hover:bg-gray-50">
          Cancel
        </button>
        <button onClick={save} disabled={saving}
          className="rounded-lg bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── Column configurator ─────────────────────────────────────────────────────

function ColumnConfig({ config, onChange }: {
  config: ColConfig;
  onChange: (cfg: ColConfig) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dragIdx, setDragIdx] = useState<number | null>(null);

  const ordered = config.order.map(k => ALL_COLUMNS.find(c => c.key === k)!).filter(Boolean);

  const toggle = (key: string) => {
    const vis = config.visible.includes(key)
      ? config.visible.filter(k => k !== key)
      : [...config.visible, key];
    const next = { ...config, visible: vis };
    onChange(next); saveColConfig(next);
  };

  const move = (from: number, to: number) => {
    const order = [...config.order];
    const [item] = order.splice(from, 1);
    order.splice(to, 0, item);
    const next = { ...config, order };
    onChange(next); saveColConfig(next);
  };

  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 flex items-center gap-1">
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
        Columns
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-1 w-56 rounded-xl border border-gray-200 bg-white shadow-lg p-2 max-h-80 overflow-y-auto">
            <p className="text-[10px] uppercase text-gray-400 font-semibold px-2 py-1">Drag to reorder</p>
            {ordered.map((col, i) => (
              <div key={col.key}
                draggable
                onDragStart={() => setDragIdx(i)}
                onDragOver={e => e.preventDefault()}
                onDrop={() => { if (dragIdx !== null && dragIdx !== i) move(dragIdx, i); setDragIdx(null); }}
                className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-grab hover:bg-gray-50 ${dragIdx === i ? 'opacity-50' : ''}`}>
                <input type="checkbox" checked={config.visible.includes(col.key)}
                  onChange={() => toggle(col.key)}
                  className="rounded border-gray-300 text-brand-600 h-3.5 w-3.5" />
                <span className="text-xs text-gray-700 flex-1">{col.label || col.key}</span>
                <svg className="w-3 h-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
                </svg>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function VelocitySparkline({ values }: { values: number[] | null }) {
  if (!values || values.length < 2) {
    return <span className="text-gray-300 text-xs">—</span>;
  }
  const W = 56;
  const H = 18;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const step = W / (values.length - 1);
  const points = values.map((v, i) => {
    const x = i * step;
    const y = H - ((v - min) / range) * H;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  // Tone based on last vs first (simple linear trend)
  const first = values[0];
  const last = values[values.length - 1];
  const delta = first > 0 ? (last - first) / first : 0;
  const stroke = delta > 0.1 ? '#059669' : delta < -0.1 ? '#dc2626' : '#6b7280';

  return (
    <svg width={W} height={H} className="inline-block align-middle" aria-label="Velocity trend">
      <polyline fill="none" stroke={stroke} strokeWidth="1.2" points={points} />
    </svg>
  );
}

function MarketplaceBadge({ id }: { id: string }) {
  const label = MARKETPLACE_LABELS[id] ?? id.slice(0, 4);
  const colors: Record<string, string> = {
    US: 'bg-blue-50 text-blue-700',
    CA: 'bg-red-50 text-red-700',
    MX: 'bg-green-50 text-green-700',
    UK: 'bg-purple-50 text-purple-700',
    DE: 'bg-yellow-50 text-yellow-700',
    FR: 'bg-indigo-50 text-indigo-700',
    IT: 'bg-emerald-50 text-emerald-700',
    ES: 'bg-orange-50 text-orange-700',
    NL: 'bg-sky-50 text-sky-700',
    SE: 'bg-cyan-50 text-cyan-700',
  };
  return (
    <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${colors[label] ?? 'bg-gray-50 text-gray-600'}`}>
      {label}
    </span>
  );
}

function UrgencyBadge({ urgency }: { urgency: string }) {
  const styles: Record<string, string> = {
    critical: 'bg-red-100 text-red-700',
    warning: 'bg-yellow-100 text-yellow-700',
    monitor: 'bg-blue-100 text-blue-700',
    healthy: 'bg-green-100 text-green-700',
    'no-sales': 'bg-gray-100 text-gray-500',
  };
  return (
    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${styles[urgency] ?? styles['no-sales']}`}>
      {urgency}
    </span>
  );
}

function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color?: 'green' | 'red' | 'yellow' | 'orange';
}) {
  const colorClass = color === 'green' ? 'text-green-600'
    : color === 'red' ? 'text-red-600'
    : color === 'yellow' ? 'text-yellow-600'
    : color === 'orange' ? 'text-orange-600'
    : 'text-gray-900';

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className={`mt-1 text-lg font-semibold ${colorClass}`}>{value}</p>
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div>
          <div className="h-5 w-40 rounded bg-gray-200" />
          <div className="mt-2 h-4 w-72 rounded bg-gray-200" />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="h-20 rounded-xl bg-gray-200" />
        ))}
      </div>
      <div className="h-64 rounded-xl bg-gray-200" />
    </div>
  );
}

function fmtCurrency(val: number): string {
  return '$' + val.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
